import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import EventCard from "@/components/EventCard";
import { Users, MapPin, Phone, Mail, Globe, Facebook, Instagram, HeartHandshake, Share2, CalendarDays, Newspaper, Ticket } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { SuggestLink } from "@/components/SuggestLink";

export default function ClubDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/public/clubs/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useSeo({
    title: data ? `${data.club.name} — club en Eure-et-Loir | PROXIreduc-28` : "Club",
    description: data?.club?.description || "Page vitrine d'un club locale d'Eure-et-Loir.",
    canonicalPath: `/clubs/${slug}`,
  });

  if (notFound) return (
    <div className="max-w-xl mx-auto py-20 px-4 text-center" data-testid="club-notfound">
      <Users size={40} className="mx-auto text-slate-300" />
      <h1 className="mt-4 font-heading text-2xl font-bold">Club introuvable</h1>
      <Link to="/clubs" className="mt-4 inline-block text-indigo-700 font-semibold hover:underline">← Retour à l'annuaire</Link>
    </div>
  );
  if (!data) return <div className="p-10 text-center text-slate-500">Chargement…</div>;

  const a = data.club;
  const shareFb = () => {
    const url = `${window.location.origin}/clubs/${a.slug}`;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank", "noopener,width=680,height=640");
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/clubs/${a.slug}`); toast.success("Lien copié !"); }
    catch { toast.error("Impossible de copier le lien."); }
  };
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([a.address, a.postal_code, a.city].filter(Boolean).join(" "))}`;

  return (
    <div data-testid="club-detail-page">
      <div className="relative h-56 md:h-72 bg-indigo-100">
        {a.cover_url
          ? <img src={a.cover_url} alt={a.name} className="w-full h-full object-cover" />
          : <div className="w-full h-full grid place-items-center text-indigo-200"><Users size={64} /></div>}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="-mt-12 relative flex flex-col md:flex-row md:items-end gap-4">
          <div className="w-24 h-24 rounded-2xl bg-white border-4 border-white shadow-lg overflow-hidden shrink-0 grid place-items-center">
            {a.logo_url ? <img src={a.logo_url} alt="" className="w-full h-full object-cover" /> : <Users size={34} className="text-indigo-400" />}
          </div>
          <div className="flex-1 pb-1">
            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800">{a.category_label}</span>
            <h1 className="mt-2 font-heading text-3xl md:text-4xl font-extrabold text-slate-900" data-testid="club-name">{a.name}</h1>
            {a.description && <p className="mt-1 text-slate-600">{a.description}</p>}
          </div>
          <div className="flex gap-2 pb-1">
            {a.helloasso_url && (
              <a href={a.helloasso_url} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="club-helloasso-top"><Ticket className="w-4 h-4 mr-1" /> Adhérer / Billetterie</Button>
              </a>
            )}
            {a.donation_url && (
              <a href={a.donation_url} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="club-donate-top"><HeartHandshake className="w-4 h-4 mr-1" /> Faire un don</Button>
              </a>
            )}
            <Button variant="outline" size="sm" onClick={shareFb} data-testid="club-share-fb"><Facebook className="w-4 h-4 mr-1" /> Partager</Button>
            <Button variant="outline" size="sm" onClick={copyLink} data-testid="club-copy-link"><Share2 className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-3 gap-6 pb-16">
          <div className="lg:col-span-2 space-y-8">
            {a.presentation && (
              <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="club-presentation">
                <h2 className="font-heading font-bold text-lg mb-3">Présentation</h2>
                <p className="text-slate-700 whitespace-pre-line leading-relaxed">{a.presentation}</p>
              </section>
            )}

            <section data-testid="club-events">
              <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><CalendarDays size={18} className="text-indigo-700" /> Prochains événements</h2>
              {data.events.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-6 text-slate-500 text-sm">Aucun événement programmé pour l'instant.</div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">{data.events.map((e) => (<EventCard key={e.id} event={e} />))}</div>
              )}
            </section>

            <section data-testid="club-news">
              <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><Newspaper size={18} className="text-indigo-700" /> Actualités</h2>
              {data.news.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-6 text-slate-500 text-sm">Pas encore d'actualité publiée.</div>
              ) : (
                <div className="space-y-4">
                  {data.news.map((n) => (
                    <Link key={n.id} to={`/clubs/${a.slug}/actualites/${n.slug}`} data-testid={`club-news-${n.id}`}
                          className="card-hover flex gap-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
                      {n.cover_image && <img src={n.cover_image} alt="" className="w-28 h-28 object-cover shrink-0" />}
                      <div className="p-4 min-w-0">
                        <h3 className="font-heading font-semibold text-slate-900 line-clamp-2">{n.title}</h3>
                        {n.excerpt && <p className="mt-1 text-sm text-slate-500 line-clamp-2">{n.excerpt}</p>}
                        <span className="mt-2 inline-block text-xs text-indigo-700 font-semibold">Lire l'actualité →</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            {(a.gallery || []).length > 0 && (
              <section data-testid="club-gallery">
                <h2 className="font-heading font-bold text-lg mb-3">Galerie</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {a.gallery.map((g, i) => (<img key={i} src={g} alt="" className="rounded-xl object-cover aspect-square w-full border border-slate-100" loading="lazy" />))}
                </div>
              </section>
            )}
          </div>

          <aside className="space-y-4">
            {a.helloasso_url && (
              <div className="bg-gradient-to-br from-indigo-600 to-violet-600 text-white rounded-2xl p-5" data-testid="club-helloasso-card">
                <Ticket size={22} />
                <h3 className="mt-2 font-heading font-bold">Adhérer & billetterie</h3>
                <p className="text-sm text-indigo-50 mt-1">Adhésions, billets et paiements en ligne, en toute sécurité via HelloAsso.</p>
                <a href={a.helloasso_url} target="_blank" rel="noopener noreferrer">
                  <Button className="mt-3 w-full bg-white text-indigo-700 hover:bg-indigo-50" data-testid="club-helloasso-btn">Accéder à HelloAsso</Button>
                </a>
              </div>
            )}
            {a.donation_url && (
              <div className="bg-gradient-to-br from-rose-600 to-rose-700 text-white rounded-2xl p-5" data-testid="club-donate-card">
                <HeartHandshake size={22} />
                <h3 className="mt-2 font-heading font-bold">Soutenez {a.name}</h3>
                <p className="text-sm text-rose-50 mt-1">Votre don aide le club à poursuivre ses actions sur le territoire.</p>
                <a href={a.donation_url} target="_blank" rel="noopener noreferrer">
                  <Button className="mt-3 w-full bg-white text-rose-700 hover:bg-rose-50" data-testid="club-donate-btn">Faire un don</Button>
                </a>
              </div>
            )}
            {(a.join_url || a.looking_for_volunteers) && (
              <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-2xl p-5" data-testid="club-join">
                <HeartHandshake size={22} />
                <h3 className="mt-2 font-heading font-bold">Rejoignez-nous</h3>
                {a.looking_for_volunteers && <p className="text-sm text-indigo-50 mt-1">Ce club recherche des bénévoles !</p>}
                {a.membership_info && <p className="text-sm text-indigo-50 mt-1 whitespace-pre-line">{a.membership_info}</p>}
                {a.join_url && (
                  <a href={a.join_url} target="_blank" rel="noopener noreferrer">
                    <Button className="mt-3 w-full bg-white text-indigo-700 hover:bg-indigo-50" data-testid="club-join-btn">Adhérer / Devenir bénévole</Button>
                  </a>
                )}
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="club-contact">
              <h3 className="font-heading font-bold">Coordonnées</h3>
              {(a.address || a.city) && (
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 text-sm text-slate-700 hover:text-indigo-700">
                  <MapPin size={16} className="mt-0.5 shrink-0" /><span>{[a.address, [a.postal_code, a.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</span>
                </a>
              )}
              {a.phone && <a href={`tel:${a.phone}`} className="flex items-center gap-2 text-sm text-slate-700 hover:text-indigo-700"><Phone size={16} /> {a.phone}</a>}
              {a.email && <a href={`mailto:${a.email}`} className="flex items-center gap-2 text-sm text-slate-700 hover:text-indigo-700 break-all"><Mail size={16} /> {a.email}</a>}
              {a.website && <a href={a.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-slate-700 hover:text-indigo-700 break-all"><Globe size={16} /> Site internet</a>}
              {(a.facebook_url || a.instagram_url) && (
                <div className="flex gap-2 pt-1">
                  {a.facebook_url && <a href={a.facebook_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 grid place-items-center rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100"><Facebook size={16} /></a>}
                  {a.instagram_url && <a href={a.instagram_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 grid place-items-center rounded-lg bg-pink-50 text-pink-600 hover:bg-pink-100"><Instagram size={16} /></a>}
                </div>
              )}
            </div>
            <Link to="/clubs" className="block text-center text-sm text-slate-500 hover:text-indigo-700">← Tous les clubs</Link>
          </aside>
        </div>
        <SuggestLink />
      </div>
    </div>
  );
}
